import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { DatePicker, Select } from 'antd';
import dayjs from 'dayjs';
import Breadcrumbs from '../../components/Breadcrumbs';
import { FieldOwnerIcon, FieldLevelIcon, RocketIcon, ArrowLeftIcon } from '../../assets/icons';
import { useGetEcLevelsQuery } from '../../features/territory/territoryApi';
import {
  useGetUsersInAreaQuery,
  useGetEngagementQuery,
  useCreateEngagementMutation,
  useUpdateEngagementMutation,
  areaUserName,
  engArea,
  engName,
  type Engagement,
} from '../../features/engagements/engagementsApi';

const INPUT =
  'w-full rounded-md border bg-white px-[13px] py-3 text-base text-ink shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] placeholder:text-[#6b7280] transition-colors focus:outline-none focus:ring-2';
const inputCls = (invalid?: string) =>
  [
    INPUT,
    invalid
      ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
      : 'border-line focus:border-brand focus:ring-brand/15',
  ].join(' ');

const STATUSES = ['Active', 'Upcoming', 'Completed', 'Closed'];

interface EngagementFormValues {
  name: string;
  engagementDate: string;
  areaCode: string;
  owner: number | '';
  ecLevelId: number | '';
  status: string;
  description: string;
}

const validationSchema = Yup.object({
  name: Yup.string().trim().required('Engagement name is required'),
  areaCode: Yup.string().trim().required('Operational boundary is required'),
  owner: Yup.mixed().test('required', 'Engagement owner is required', (v) => v !== '' && v != null),
  ecLevelId: Yup.mixed().test('required', 'Level is required', (v) => v !== '' && v != null),
  engagementDate: Yup.string().required('Date is required'),
});

function Card({ eyebrow, children }: { eyebrow: string; children: ReactNode }) {
  return (
    <section className="overflow-hidden rounded-lg border border-line-soft bg-white shadow-card">
      <div className="border-b border-[#f3f4f6] px-6 py-5">
        <span className="text-xs font-semibold uppercase tracking-[0.6px] text-[#6b7280]">{eyebrow}</span>
      </div>
      <div className="p-6">{children}</div>
    </section>
  );
}

function Label({ children, required }: { children: ReactNode; required?: boolean }) {
  return (
    <span className="text-sm font-medium text-[#374151]">
      {children}
      {required && <span className="text-[#ef4444]"> *</span>}
    </span>
  );
}

export default function CreateEngagementPage() {
  const navigate = useNavigate();
  const { id: editId } = useParams<{ id: string }>();
  const isEdit = Boolean(editId);

  const { data: ecLevels } = useGetEcLevelsQuery();
  const { data: editEngagement } = useGetEngagementQuery(editId!, { skip: !isEdit });
  const [createEngagement, { isLoading: isCreating }] = useCreateEngagementMutation();
  const [updateEngagement, { isLoading: isUpdating }] = useUpdateEngagementMutation();
  const saving = isCreating || isUpdating;

  const formik = useFormik<EngagementFormValues>({
    initialValues: {
      name: '',
      engagementDate: '',
      areaCode: '',
      owner: '',
      ecLevelId: '',
      status: 'Active',
      description: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      const payload = {
        name: values.name.trim(),
        description: values.description.trim(),
        status: values.status,
        engagement_date: values.engagementDate,
        owner: Number(values.owner),
        ec_location_level_id: Number(values.ecLevelId),
        area_code: values.areaCode.trim(),
      };
      try {
        if (isEdit && editId) {
          await updateEngagement({ id: editId, patch: payload }).unwrap();
        } else {
          await createEngagement(payload).unwrap();
        }
        navigate('/user/engagements');
      } catch {
        /* surfaced via inline errors / retry */
      }
    },
  });

  const { values, errors, touched, handleChange, handleBlur, setFieldValue, setFieldTouched } = formik;

  // Owner options — users inside the entered boundary.
  const { data: areaUsers } = useGetUsersInAreaQuery(
    { areaCode: values.areaCode.trim() },
    { skip: values.areaCode.trim() === '' },
  );

  // Edit mode: prefill from the loaded engagement.
  useEffect(() => {
    if (!editEngagement) return;
    const e = editEngagement as Engagement;
    setFieldValue('name', engName(e) === 'Untitled' ? '' : engName(e));
    setFieldValue('description', e.description ?? '');
    setFieldValue('status', e.status || 'Active');
    setFieldValue('engagementDate', e.engagement_date ?? '');
    setFieldValue('areaCode', engArea(e));
    if (e.ec_location_level_id != null) setFieldValue('ecLevelId', e.ec_location_level_id);
    const ownerId = typeof e.owner === 'object' ? e.owner?.id : e.owner;
    if (ownerId != null) setFieldValue('owner', Number(ownerId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editEngagement]);

  const errorOf = (name: keyof EngagementFormValues) =>
    touched[name] && errors[name] ? (errors[name] as string) : undefined;

  return (
    <form onSubmit={formik.handleSubmit} noValidate className="flex flex-col gap-6" data-testid="create-engagement">
      {/* Heading */}
      <div className="flex flex-col gap-1">
        <Breadcrumbs
          items={[
            { label: 'Engagements', to: '/user/engagements' },
            { label: isEdit ? 'Edit Engagement' : 'Create Engagement' },
          ]}
        />
        <div className="flex items-center gap-3">
          <button
            type="button"
            aria-label="Back to engagements"
            onClick={() => navigate('/user/engagements')}
            className="text-ink-900 transition-colors hover:text-brand"
          >
            <ArrowLeftIcon className="size-5" />
          </button>
          <h1 className="text-2xl font-semibold tracking-[-0.4px] text-ink">
            {isEdit ? 'Edit Engagement' : 'Create Engagement'}
          </h1>
        </div>
      </div>

      {/* Basic information */}
      <Card eyebrow="Basic Information">
        <div className="flex flex-col gap-6">
          <label className="flex flex-col gap-2">
            <Label required>Engagement Name</Label>
            <input
              name="name"
              placeholder="Enter engagement name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              className={inputCls(errorOf('name'))}
            />
            {errorOf('name') && <span className="text-xs font-medium text-[#ba1a1a]">{errorOf('name')}</span>}
          </label>

          <div className="flex flex-col gap-6 sm:flex-row">
            <label className="flex flex-1 flex-col gap-2">
              <Label required>Date</Label>
              <DatePicker
                className="w-full"
                format="MM/DD/YYYY"
                placeholder="mm/dd/yyyy"
                status={errorOf('engagementDate') ? 'error' : undefined}
                value={values.engagementDate ? dayjs(values.engagementDate) : null}
                onChange={(d) => setFieldValue('engagementDate', d ? d.format('YYYY-MM-DD') : '')}
                onBlur={() => setFieldTouched('engagementDate', true)}
              />
              {errorOf('engagementDate') && (
                <span className="text-xs font-medium text-[#ba1a1a]">{errorOf('engagementDate')}</span>
              )}
            </label>
            <label className="flex flex-1 flex-col gap-2">
              <Label required>Operational Boundary</Label>
              <input
                name="areaCode"
                placeholder="Area code (e.g. S20_08)"
                value={values.areaCode}
                onChange={(e) => {
                  setFieldValue('areaCode', e.target.value);
                  setFieldValue('owner', ''); // owner list depends on the boundary
                }}
                onBlur={handleBlur}
                className={inputCls(errorOf('areaCode'))}
              />
              {errorOf('areaCode') && (
                <span className="text-xs font-medium text-[#ba1a1a]">{errorOf('areaCode')}</span>
              )}
            </label>
          </div>

          <div className="flex flex-col gap-6 sm:flex-row">
            <div className="flex flex-1 flex-col gap-2">
              <Label required>Engagement Owner</Label>
              <Select
                className="w-full"
                placeholder={values.areaCode.trim() ? 'Search and select owner' : 'Enter a boundary first'}
                showSearch
                optionFilterProp="label"
                disabled={values.areaCode.trim() === ''}
                status={errorOf('owner') ? 'error' : undefined}
                prefix={<FieldOwnerIcon className="size-5 text-[#9ca3af]" />}
                value={values.owner === '' ? undefined : values.owner}
                onChange={(v) => setFieldValue('owner', v)}
                onBlur={() => setFieldTouched('owner', true)}
                options={(areaUsers ?? []).map((u) => ({ value: u.id, label: areaUserName(u) }))}
              />
              {errorOf('owner') ? (
                <span className="text-xs font-medium text-[#ba1a1a]">{errorOf('owner')}</span>
              ) : (
                <span className="text-xs text-[#6b7280]">
                  Owners are the users inside the selected boundary.
                </span>
              )}
            </div>

            <div className="flex flex-1 flex-col gap-2">
              <Label required>Level</Label>
              <Select
                className="w-full"
                placeholder="Select level"
                status={errorOf('ecLevelId') ? 'error' : undefined}
                prefix={<FieldLevelIcon className="h-5 w-4 text-[#9ca3af]" />}
                value={values.ecLevelId === '' ? undefined : values.ecLevelId}
                onChange={(v) => setFieldValue('ecLevelId', v)}
                onBlur={() => setFieldTouched('ecLevelId', true)}
                options={(ecLevels ?? []).map((l) => ({
                  value: l.level_rank ?? l.id,
                  label: l.level_name || `Level ${l.level_rank ?? l.id}`,
                }))}
              />
              {errorOf('ecLevelId') && (
                <span className="text-xs font-medium text-[#ba1a1a]">{errorOf('ecLevelId')}</span>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Engagement status */}
      <Card eyebrow="Engagement Status">
        <div className="flex flex-wrap items-center gap-8">
          {STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setFieldValue('status', s)}
              className="flex items-center gap-2"
            >
              <span
                className={[
                  'flex size-[18px] items-center justify-center rounded-full border transition-colors',
                  values.status === s ? 'border-[#003da5] bg-[#003da5]' : 'border-[#cbd5e1] bg-white',
                ].join(' ')}
              >
                {values.status === s && <span className="size-2.5 rounded-full bg-white" />}
              </span>
              <span className="text-sm text-[#374151]">{s}</span>
            </button>
          ))}
        </div>
      </Card>

      {/* Description */}
      <Card eyebrow="Description">
        <label className="flex flex-col gap-2">
          <Label>Engagement Description</Label>
          <textarea
            name="description"
            rows={5}
            placeholder="Enter engagement description..."
            value={values.description}
            onChange={handleChange}
            onBlur={handleBlur}
            className={[inputCls(), 'resize-none placeholder:text-[#9ca3af]'].join(' ')}
          />
        </label>
      </Card>

      {/* Actions */}
      <div className="flex flex-wrap justify-end gap-4">
        <button
          type="button"
          onClick={() => navigate('/user/engagements')}
          className="rounded-lg border border-brand px-8 py-3 text-base text-brand transition-colors hover:bg-brand-shade"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex items-center gap-2 rounded-lg bg-brand px-8 py-3 text-base text-white shadow-[0px_10px_15px_-3px_rgba(0,61,155,0.2),0px_4px_6px_-4px_rgba(0,61,155,0.2)] transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? 'Saving…' : isEdit ? 'Update Engagement' : 'Create Engagement'}
          <RocketIcon className="size-[15px]" />
        </button>
      </div>
    </form>
  );
}
